import { useEmonPriceContext } from '@/contexts/EmonPriceContext';
import { Badge } from '@/components/ui/badge';
import { RefreshCw, Clock, Database, AlertTriangle, Zap } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

interface EmonPriceIndicatorProps {
  className?: string;
  showRefreshButton?: boolean;
  compact?: boolean;
}

export function EmonPriceIndicator({ 
  className, 
  showRefreshButton = true,
  compact = false 
}: EmonPriceIndicatorProps) {
  const { 
    rate, 
    lastUpdated, 
    isLoading, 
    dexSource, 
    source, 
    refresh,
    triggerPriceUpdate 
  } = useEmonPriceContext();

  type SourceVariant = "primary" | "accent" | "muted";

  // Get source display info
  const getSourceInfo = (): { label: string; icon: typeof Database; variant: SourceVariant } => {
    if (!dexSource) {
      return { label: "Cache", icon: Database, variant: "muted" };
    }

    const sourceLower = dexSource.toLowerCase();

    if (sourceLower.includes("jupiter")) {
      return { label: "Jupiter", icon: Zap, variant: "primary" };
    }
    if (sourceLower.includes("dexscreener")) {
      return { label: "DexScreener", icon: Database, variant: "primary" };
    }
    if (sourceLower.includes("fallback")) {
      return { label: "Fallback", icon: AlertTriangle, variant: "accent" };
    }

    return { label: dexSource, icon: Database, variant: "muted" };
  };

  const sourceInfo = getSourceInfo();
  const SourceIcon = sourceInfo.icon;

  const sourceStyles: Record<SourceVariant, { icon: string; badge: string }> = {
    primary: {
      icon: "text-primary",
      badge: "bg-primary/10 text-primary border-primary/20",
    },
    accent: {
      icon: "text-accent",
      badge: "bg-accent/10 text-accent border-accent/20",
    },
    muted: {
      icon: "text-muted-foreground",
      badge: "bg-muted text-muted-foreground border-border",
    },
  };

  // Format time ago
  const timeAgo = lastUpdated ? formatDistanceToNow(lastUpdated, { addSuffix: true }) : "never";

  if (compact) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <div
              className={cn(
                "flex items-center gap-1.5 text-xs text-muted-foreground cursor-help",
                className,
              )}
            >
              <SourceIcon className={cn("w-3 h-3", sourceStyles[sourceInfo.variant].icon)} />
              <span className="font-mono">${rate.toFixed(8)}</span>
              {isLoading && <RefreshCw className="w-3 h-3 animate-spin" />}
            </div>
          </TooltipTrigger>
          <TooltipContent side="bottom" className="text-xs">
            <div className="space-y-1">
              <div>
                Source: <span className="font-medium">{sourceInfo.label}</span>
              </div>
              <div>
                Updated: <span className="font-medium">{timeAgo}</span>
              </div>
            </div>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return (
    <div
      className={cn(
        "inline-flex items-center gap-4 px-5 py-3 rounded-2xl",
        "bg-card/90 border border-border shadow-card",
        "backdrop-blur-sm",
        className,
      )}
    >
      {/* EMON Logo + Price */}
      <div className="flex items-center gap-3">
        <div className="w-9 h-9 rounded-xl gradient-primary flex items-center justify-center shadow-lg shadow-primary/20">
          <Zap className="w-5 h-5 text-primary-foreground" />
        </div>
        <div className="flex flex-col">
          <span className="text-[10px] uppercase tracking-widest text-muted-foreground font-medium">
            EMON/USD
          </span>
          <span className="font-mono text-base font-bold text-foreground tracking-tight">
            ${rate.toFixed(8)}
          </span>
        </div>
      </div>

      {/* Divider */}
      <div className="w-px h-8 bg-gradient-to-b from-transparent via-border to-transparent" />

      {/* Source badge */}
      <div className="flex items-center gap-2">
        <div
          className={cn(
            "flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium border",
            sourceStyles[sourceInfo.variant].badge,
          )}
        >
          <SourceIcon className="w-3.5 h-3.5" />
          {sourceInfo.label}
        </div>
      </div>

      {/* Divider */}
      <div className="w-px h-8 bg-gradient-to-b from-transparent via-border to-transparent" />

      {/* Last updated */}
      <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
        <Clock className="w-3.5 h-3.5" />
        <span>{timeAgo}</span>
      </div>

      {/* Refresh button */}
      {showRefreshButton && (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="outline"
                size="icon"
                aria-label="Refresh EMON price"
                className="h-8 w-8 rounded-xl"
                onClick={triggerPriceUpdate}
                disabled={isLoading}
              >
                <RefreshCw className={cn("w-4 h-4", isLoading && "animate-spin")} />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom" className="text-xs">
              Refresh price
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}
    </div>
  );
}
